import styles from './OrderSummary.module.scss';

import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { PromoSummary } from 'components/Cart/OrderSummary/PromoSummary/PromoSummary';
import { TextV2 } from 'components/TextV2/TextV2';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { useDCS } from 'hooks/useDCS';
import { IntercomWrapper } from 'services/intercom';
import { OrderSummaryButton } from './OrderSummaryButton/OrderSummaryButton';
import { ORDER_SUMMARY_TEST_ID } from './OrderSummary.const';
import { SubscriptionPaymentPlans } from './SubscriptionPaymentPlans/SubscriptionPaymentPlans';
import { HardwarePayment } from './HardwarePayment/HardwarePayment';
import { SoftwarePlan } from './SoftwarePlan/SoftwarePlan';
import { PaymentsToggle } from './PaymentsToggle/PaymentsToggle';
import { usePaymentsToggle } from './PaymentsToggle/usePaymentsToggle/usePaymentsToggle';
import {
  getCartProductsAndAccessories,
  isAccountOnlyCart,
  isPayMonthlyAndCartHasProducts,
} from 'components/Cart/utils/getCartProductsAndAccessoriesUtils';
import { CreditCardIcons } from './CreditCardIcons/CreditCardIcons';
import { EarlyCancellationFee } from './EarlyCancellationFee/EarlyCancellationFee';
import { FetchingPromoInternalProvider } from './PromoSummary/FetchingPromoContext/useFetchingPromoInternal';
import { useFeatureFlagsContext } from 'components/FeatureFlags/FeatureFlags';
import { useCartContext } from 'components/Cart/hooks/useCartContext';

interface IOrderSummaryProps {
  isContinueButtonDisabled?: boolean;
  continueButtonClickHandler?: React.MouseEventHandler<HTMLButtonElement>;
  backButtonVisible?: boolean;
  isCheckout?: boolean;
  isPayMonthly?: boolean;
}

export const OrderSummary: React.FC<IOrderSummaryProps> = ({
  isContinueButtonDisabled,
  continueButtonClickHandler,
  isCheckout,
}) => {
  const orderSummaryRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { isFlagEnabled } = useFeatureFlagsContext();
  const { cart, checkout } = useCartContext();

  const [userMustContactSales, setUserMustContactSales] = useState<boolean>(false);

  const { productsPricings, bundlesPricings } = useDCS({
    bundleIds: [cart.cartMetadata?.id ?? ''],
    productIds: cart.products.map(p => p.id),
  });
  const { browser } = useBreakpoints();
  const isMobile = browser.is.mobile;
  const { isPayMonthly } = usePaymentsToggle();
  const isShippingFeeEnabled = isFlagEnabled('shipping-fee-enabled');

  const { payInFullProductsAndAccessories, payMonthlyProductsAndAccessories } =
    getCartProductsAndAccessories(cart);
  const showDueTodaySectionForPayInFullProductsAndAccessories = isPayMonthlyAndCartHasProducts(
    isPayMonthly,
    payInFullProductsAndAccessories,
  );

  useDeepCompareEffect(() => {
    setUserMustContactSales(
      bundlesPricings.some(b => b && !b.isSelfServicePurchaseEnabled) ||
        productsPricings.some(p => p && !p.isSelfServicePurchaseEnabled),
    );
  }, [cart, productsPricings, bundlesPricings]);

  useEffect(() => {
    if (isMobile) {
      IntercomWrapper.update({
        vertical_padding: 100,
      });
    }

    return () => {
      IntercomWrapper.update({
        vertical_padding: 20,
      });
    };
  }, [isMobile]);

  const hasHardware = !isAccountOnlyCart(cart);

  return (
    <div className={styles.orderSummaryTopMargin}>
      {payMonthlyProductsAndAccessories.length > 0 && (
        <PaymentsToggle className={cx(styles.orderSummary, styles.wrapper, styles.wrapperNoBorderRadius)} />
      )}
      <section
        ref={orderSummaryRef}
        className={cx(styles.orderSummary, { [styles.orderSummaryCheckout]: isCheckout })}
        data-testid={ORDER_SUMMARY_TEST_ID.order}
      >
        <div className={styles.wrapper} ref={wrapperRef}>
          {hasHardware && (
            <section className={styles.sectionFirstChild}>
              {isPayMonthly && payMonthlyProductsAndAccessories.length > 0 ? (
                <>
                  <SubscriptionPaymentPlans />
                  {showDueTodaySectionForPayInFullProductsAndAccessories && (
                    <div className={styles.subscriptionsHardwareWrapper}>
                      <HardwarePayment />
                    </div>
                  )}
                </>
              ) : (
                <HardwarePayment />
              )}
              <div className={styles.container}>
                <TextV2
                  variant="body"
                  tagName="p"
                  value={t('order-summary.shipping')}
                  className={styles.containerLabel}
                />
                <TextV2
                  variant="body"
                  tagName="p"
                  value={isShippingFeeEnabled ? t('order-summary.shipping-fee') : t('order-summary.free')}
                />
              </div>
              <div className={cx(styles.container, styles.containerBordered)}>
                <TextV2
                  variant="body"
                  tagName="p"
                  value={t('order-summary.tax-text')}
                  className={styles.containerLabel}
                />
                <TextV2
                  variant="body"
                  tagName="p"
                  value={t('order-summary.tax-text-value')}
                  className={styles.containerTextAlign}
                />
              </div>
            </section>
          )}
          {(!isPayMonthly || payMonthlyProductsAndAccessories.length === 0) && (
            <>
              <SoftwarePlan isCheckout={isCheckout} />
              {hasHardware && (
                <FetchingPromoInternalProvider>
                  {(!isCheckout || checkout.promo.applied) && (
                    <PromoSummary promoBoxVisible={!isCheckout} data-testid={ORDER_SUMMARY_TEST_ID.promo} />
                  )}
                </FetchingPromoInternalProvider>
              )}
            </>
          )}
          <OrderSummaryButton
            continueButtonClickHandler={continueButtonClickHandler}
            userMustContactSales={userMustContactSales}
            isContinueButtonDisabled={isContinueButtonDisabled}
          />
          <CreditCardIcons />
        </div>
      </section>
      {payMonthlyProductsAndAccessories.length > 0 && <EarlyCancellationFee />}
    </div>
  );
};

OrderSummary.displayName = 'OrderSummary';
